import { useQuery } from '@apollo/client'
import { GET_GOAL_INFO } from '@lib/graphql/queries/Goals'
import { GET_TASK_INFO } from '@lib/graphql/queries/Task'
import { useMemo } from 'react'

export const SYSTEM_MESSAGE_PREFIX = '##SYSTEM'
export type ConvertedEntityType = 'goal' | 'task' | 'todo'

interface UseCommentSystemMessageReturn {
  isSystemMessage: boolean
  id: string
  hasError: boolean
  name: string | undefined
  type: ConvertedEntityType
}

const useCommentSystemMessage = (
  commentText: string,
): UseCommentSystemMessageReturn => {
  const isSystemMessage = commentText?.startsWith(SYSTEM_MESSAGE_PREFIX)

  const entityId = useMemo(
    () => commentText.match(new RegExp(/[a-f0-9-]{36}/, 'g'))?.[0] ?? '',
    [commentText],
  )

  const commentInitialText = useMemo(
    () =>
      commentText.match(
        new RegExp(/(?<=converted_to_|created_).*(?=:)/, 'g'),
      )?.[0] ?? '',
    [commentText],
  )

  const isGoal = isSystemMessage && commentInitialText === 'goal'
  const isTask = isSystemMessage && commentInitialText === 'task'

  const { data: goalData, error: goalError } = useQuery(GET_GOAL_INFO, {
    variables: { input: { id: entityId } },
    skip: !isGoal,
  })

  const { data: taskData, error: taskError } = useQuery(GET_TASK_INFO, {
    variables: { input: { id: entityId } },
    skip: !isTask,
  })

  const hasError = useMemo(() => {
    const gqlErrors = goalError?.graphQLErrors || taskError?.graphQLErrors || []
    return gqlErrors.length > 0
  }, [goalError, taskError])

  const getEntityType = useMemo((): ConvertedEntityType => {
    if (isGoal) return 'goal'
    if (isTask) {
      if (taskData?.getTask?.milestone?.id || taskData?.getTask?.goal?.id)
        return 'task'
    } else {
      return 'todo'
    }
  }, [isGoal, isTask, taskData])

  return {
    isSystemMessage,
    id: entityId,
    hasError,
    name: goalData?.getGoal?.name || taskData?.getTask?.name,
    type: getEntityType,
  }
}

export default useCommentSystemMessage
